import { Badge, Button, Card, CloseButton, Group, Pagination, Progress, Stack, Table, Tabs, Text, TextInput, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconFileDescription, IconPlus, IconSearch } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { REQUEST_FLAG_LABELS, REQUEST_FLAGS, type ClientStage, type RequestFlag, type RequestSummaryDto, type RequestView } from '@mexp/shared';
import { http } from '@/api/client';
import { useAuth } from '@/auth/AuthProvider';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/States';
import { notificationKeys } from '@/layout/NotificationBell';
import { formatDate, fromNow } from '@/lib/format';
import { useCountries } from '@/features/shared/lookups';
import {
  filtering,
  NO_FILTERS,
  readRequisitionFilters,
  RequisitionFilters,
  writeRequisitionFilters,
  type RequisitionFilterValues,
} from '@/features/portal/RequisitionFilters';
import { STAGE_ORDER } from '@/features/portal/stage';
import { useRequestCounts, useRequestList } from './api';
import { BdStatusBadge, DueDate, RequestStatusBadge } from './components/badges';

const TABS: { value: Exclude<RequestView, 'all'>; label: string; empty: string }[] = [
  { value: 'active', label: 'Active', empty: 'No active requests.' },
  { value: 'with_bd', label: 'With BD', empty: 'Nothing is waiting for BD review.' },
  { value: 'completed', label: 'Completed', empty: 'No completed requests yet.' },
  { value: 'cancelled', label: 'Cancelled', empty: 'No cancelled requests.' },
];
const PAGE_SIZE = 25;

/** The stage the client sees each request at; staff call a released one Completed. */
const FILTER_STAGES: ClientStage[] = [...STAGE_ORDER, 'cancelled'];
const STAGE_LABELS: Partial<Record<ClientStage, string>> = { completed: 'Completed' };

export function ProgressCell({ r }: { r: RequestSummaryDto }) {
  const { total, verified, submitted, returned } = r.stats;
  if (!total) {
    return (
      <Text fz="sm" c="dimmed">
        No tasks yet
      </Text>
    );
  }
  return (
    <Tooltip label={`${verified} verified · ${submitted} awaiting verification · ${returned} returned · ${total - verified - submitted - returned} to do`}>
      <Stack gap={4} miw={120}>
        <Text fz="xs" className="tabular">
          {verified} / {total} verified
        </Text>
        <Progress.Root size={6}>
          <Progress.Section value={(verified / total) * 100} color="teal" />
          <Progress.Section value={(submitted / total) * 100} color="yellow" />
          <Progress.Section value={(returned / total) * 100} color="orange" />
        </Progress.Root>
      </Stack>
    </Tooltip>
  );
}

export function RequestsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = (params.get('view') as Exclude<RequestView, 'all'>) || 'active';
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const counts = useRequestCounts();
  const countries = useCountries();
  // Stage, country and requisition type live in the URL next to the tab.
  const filters = readRequisitionFilters(params, FILTER_STAGES);
  // A worklist opened from the dashboard (open client queries, uploads to verify, …).
  const flag = (REQUEST_FLAGS as readonly string[]).includes(params.get('flag') ?? '') ? (params.get('flag') as RequestFlag) : null;
  const narrowed = filtering(filters) || !!flag;
  const write = (next: { view?: string; filters?: RequisitionFilterValues; flag?: RequestFlag | null }) => {
    const out = new URLSearchParams();
    const v = next.view ?? view;
    if (v !== 'active') out.set('view', v);
    writeRequisitionFilters(out, next.filters ?? filters);
    const f = next.flag === undefined ? flag : next.flag;
    if (f) out.set('flag', f);
    return out;
  };
  const setFilters = (next: RequisitionFilterValues) => {
    setParams(write({ filters: next }), { replace: true });
    setPage(1);
  };
  const list = useRequestList(view, q, page, PAGE_SIZE, {
    stage: filters.stage ?? undefined,
    country: filters.country ?? undefined,
    requestType: filters.type ?? undefined,
    flag: flag ?? undefined,
  });
  const queryClient = useQueryClient();
  // A new notification is news about some request: refresh the list so that request lights up without a reload.
  const unread = useQuery({ queryKey: notificationKeys.unread, queryFn: () => http.get<{ count: number }>('/notifications/unread-count'), refetchInterval: 60_000 });
  const unreadCount = unread.data?.count;
  useEffect(() => {
    if (unreadCount !== undefined) void queryClient.invalidateQueries({ queryKey: ['dr', 'list'] });
  }, [unreadCount, queryClient]);
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / PAGE_SIZE)) : 1;
  const canCreate = can('DossierRequests', 'edit');

  return (
    <>
      <PageHeader
        title="Document requests"
        actions={
          canCreate && (
            <Button component={Link} to="/internal/requests/new" leftSection={<IconPlus size={16} />}>
              New request
            </Button>
          )
        }
      />
      <Card p={0}>
        <Tabs
          value={view}
          onChange={(v) => {
            setParams(write({ view: v ?? 'active' }));
            setPage(1);
          }}
        >
          <Tabs.List px="md" pt="xs">
            {TABS.map((t) => (
              <Tabs.Tab
                key={t.value}
                value={t.value}
                rightSection={
                  counts.data?.[t.value] ? (
                    <Badge size="sm" variant={t.value === view ? 'filled' : 'light'} color={t.value === 'with_bd' ? 'violet' : undefined}>
                      {counts.data[t.value]}
                    </Badge>
                  ) : null
                }
              >
                {t.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
        <Group p="md" gap="sm" align="flex-start" wrap="wrap">
          <TextInput
            placeholder="Search code, brand/generic name, exporter or country"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(1);
            }}
            w={{ base: '100%', sm: 360 }}
          />
          <RequisitionFilters
            value={filters}
            onChange={setFilters}
            stages={FILTER_STAGES}
            stageLabels={STAGE_LABELS}
            countries={(countries.data ?? []).map((c) => c.name)}
          />
          {flag && (
            <Badge
              size="lg"
              variant="light"
              color="grape"
              tt="none"
              mt={4}
              rightSection={
                <CloseButton
                  size="xs"
                  variant="transparent"
                  aria-label="Show every request"
                  onClick={() => {
                    setParams(write({ flag: null }), { replace: true });
                    setPage(1);
                  }}
                />
              }
            >
              {REQUEST_FLAG_LABELS[flag]}
            </Badge>
          )}
        </Group>
        {list.isLoading ? (
          <LoadingState />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data?.items.length ? (
          <EmptyState
            icon={IconFileDescription}
            title={
              q || narrowed
                ? `No requests match your ${q && narrowed ? 'search and filters' : q ? 'search' : 'filters'}`
                : (TABS.find((t) => t.value === view)?.empty ?? 'Nothing here')
            }
            action={
              narrowed ? (
                <Button
                  variant="light"
                  onClick={() => {
                    setParams(write({ filters: NO_FILTERS, flag: null }), { replace: true });
                    setPage(1);
                  }}
                >
                  Clear filters
                </Button>
              ) : canCreate && view === 'active' && !q ? (
                <Button component={Link} to="/internal/requests/new" variant="light" leftSection={<IconPlus size={16} />}>
                  Raise a request
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="table-scroll">
            <Table miw={900}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Request</Table.Th>
                  <Table.Th>Brand / Generic name</Table.Th>
                  <Table.Th>Country</Table.Th>
                  <Table.Th>Progress</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>{view === 'completed' ? 'Completed' : view === 'cancelled' ? 'Cancelled' : 'Next due'}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.data.items.map((r) => (
                  <Table.Tr
                    key={r.id}
                    className="clickable-row"
                    data-attention={r.attention ? true : undefined}
                    onClick={() => navigate(`/internal/requests/${r.id}`)}
                  >
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        {r.attention && <span className="attention-dot" aria-hidden />}
                        <Text fw={600} fz="sm" ff="monospace">
                          {r.code}
                        </Text>
                        {r.origin === 'bds' && (
                          <Badge size="xs" variant="light" color="violet">
                            BDS
                          </Badge>
                        )}
                        {r.origin === 'client' && (
                          <Badge size="xs" variant="light" color="teal">
                            Client
                          </Badge>
                        )}
                      </Group>
                      <Text fz="xs" c="dimmed" title={formatDate(r.createdAt)}>
                        {r.createdBy.name} · {fromNow(r.createdAt)}
                      </Text>
                      {r.attention && (
                        <Group gap={6} wrap="nowrap" mt={4}>
                          <Text fz="xs" fw={600} c="brand.7" lineClamp={1} title={r.attention.title}>
                            {r.attention.title} · {fromNow(r.attention.at)}
                          </Text>
                          {r.attention.count > 1 && (
                            <Badge size="xs" variant="filled" color="brand" style={{ flex: 'none' }}>
                              {r.attention.count} new
                            </Badge>
                          )}
                        </Group>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {/* "Brand name / Generic name", then the pack size. */}
                      <Text fw={600} fz="sm">
                        {r.productName}
                      </Text>
                      {(r.genericName && !r.productName.includes(r.genericName)) || r.packSize ? (
                        <Text fz="xs">
                          {[r.genericName && !r.productName.includes(r.genericName) ? `Generic: ${r.genericName}` : null, r.packSize ? `Pack: ${r.packSize}` : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </Text>
                      ) : null}
                      <Text fz="xs" c="dimmed">
                        {r.exporterName}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm">{r.country}</Text>
                    </Table.Td>
                    <Table.Td>
                      <ProgressCell r={r} />
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Group gap={4}>
                          {/* Client requisitions skip BD: they are in progress until released, and again while a query is open. */}
                          {r.status === 'in_progress' && r.origin !== 'client' ? <BdStatusBadge status={r.bdStatus} /> : <RequestStatusBadge status={r.status} />}
                          {r.openQueries > 0 && (
                            <Badge size="sm" variant="light" color="grape">
                              {r.openQueries === 1 ? 'Query open' : `${r.openQueries} queries open`}
                            </Badge>
                          )}
                          {r.openClarifications > 0 && (
                            <Tooltip label={`${r.openClarifications === 1 ? 'A clarification' : `${r.openClarifications} clarifications`} waiting for the client's answer`}>
                              <Badge size="sm" variant="light" color="orange">
                                Waiting for the client
                              </Badge>
                            </Tooltip>
                          )}
                        </Group>
                        {r.status === 'in_progress' && (
                          <Text fz="xs" c="dimmed">
                            {r.bdReviewer ? `SC-NBD: ${r.bdReviewer.name}` : 'No SC-NBD reviewer'}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      {view === 'completed' ? (
                        <Text fz="sm">{formatDate(r.completedAt)}</Text>
                      ) : view === 'cancelled' ? (
                        <Text fz="sm">{formatDate(r.cancelledAt)}</Text>
                      ) : (
                        <DueDate date={r.nextTargetDate} compact />
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
        {list.data && list.data.total > 0 && (
          <Group justify="space-between" p="md">
            <Text fz="sm" c="dimmed">
              {list.data.total} request{list.data.total === 1 ? '' : 's'}
            </Text>
            {pages > 1 && <Pagination total={pages} value={page} onChange={setPage} />}
          </Group>
        )}
      </Card>
    </>
  );
}
